import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import ThankYouPage from './ThankYouPage';
import './App.css';

/* ============================================================
   PRODUCT CONFIG — change these in one place if needed
   ============================================================ */
const PRODUCT_NAME   = 'AirBack MAX';
const PRODUCT_ID     = 'airback-max';
const PRODUCT_IMAGE  = 'https://airback.store/cdn/shop/files/MAX_MATTBLACK_1.jpg';
const PRICE_VALUE    = 19.99;
const PRICE_TEXT     = '$19.99';
/* ============================================================ */

const stripePromise = loadStripe('pk_live_51QLY0pCrhBRXdIBeXHtQokUg4HnSXCds0dj3X6lpIoE5oFYeyBi6kDxinHdrwzO0HfgsdkKq6EgpwXRJe2oFGJEV004ryghR98');
const BACKEND = 'https://usa-backend-msk3.onrender.com';

/* ============================================================
   TRANSLATIONS — ENGLISH ONLY
   ============================================================ */
const SUPPORTED = ['en'];

const I18N = {
  en: {
    secure: 'Secure payment',
    step1: 'Contact & Delivery',
    step2: 'Payment',
    orderSummary: 'Order summary',
    contact: 'Contact',
    email: 'Email address',
    newsletter: 'Receive news and offers by email',
    delivery: 'Delivery',
    deliveryHome: 'Home delivery',
    deliverySignature: 'With signature',
    country: 'Country / Region',
    fname: 'First name',
    lname: 'Last name',
    address: 'Address',
    zip: 'Postal code',
    city: 'City',
    state: 'State',
    phone: 'Phone (optional)',
    saveData: 'Save my information for next time',
    shipping: 'Shipping',
    shippingDays: '1–2 business days',
    free: 'FREE',
    continueBtn: 'Continue to payment',
    loading: 'Loading…',
    processing: 'Processing…',
    change: 'Edit',
    securedNote: 'All transactions are secure and encrypted.',
    buyNow: 'Buy now',
    termsPre: 'By purchasing, you accept our ',
    termsAgb: 'Terms & Conditions',
    termsMid: ' and ',
    termsPrivacy: 'Privacy Policy',
    termsPost: '.',
    discountPlaceholder: 'Discount code or gift card',
    apply: 'Apply',
    subtotal: 'Subtotal',
    freeShipping: 'Free shipping',
    total: 'Total',
    trustSSL: 'SSL encryption',
    trustBuyer: 'Buyer protection',
    trustReturn: '30-day returns',
    procT: 'Processing payment…',
    procS: 'Please wait a moment',
    carryOn: 'Carry-On',
    color: 'Matte Black',
    bundleName: 'Air Pump + FREE Toiletry Bag',
    bundleFree: 'FREE',
    bundleIncluded: 'Included in the $19.99 price',
    errEmail: 'Please enter a valid email address',
    errFname: 'First name required',
    errLname: 'Last name required',
    errAddress: 'Address required',
    errZip: 'Postal code required',
    errCity: 'City required',
    errState: 'State required',
    errorPrefix: 'Error: ',
    countries: { US: 'United States' }
  }
};

function detectLang() {
  return 'en';
}

function generateVisitorId() {
  return 'V-' + Math.random().toString(36).substr(2, 9).toUpperCase();
}

const VISITOR_ID = generateVisitorId();

function fbTrack(event, params) {
  if (window.fbq) { window.fbq('track', event, params); }
  else { const i = setInterval(() => { if (window.fbq) { window.fbq('track', event, params); clearInterval(i); } }, 100); setTimeout(() => clearInterval(i), 5000); }
}

function PaymentForm({ customerId, visitorId, customerEmail, t }) {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements || !ready) return;
    setIsLoading(true);
    setMessage('');

    fbTrack('AddPaymentInfo', { currency: 'USD', value: PRICE_VALUE, content_name: PRODUCT_NAME, content_ids: [PRODUCT_ID] });

    fetch(`${BACKEND}/payment-initiated`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorId: visitorId,
        email: customerEmail
      })
    }).catch(() => {});

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}?customer_id=${customerId}&visitor_id=${visitorId}&lang=en`,
      }
    });
    if (error) { setMessage(error.message); setIsLoading(false); }
  };

  return (
    <div>
      <div className="stripe-wrap">
        <PaymentElement onReady={() => setReady(true)} options={{paymentMethodOrder: ['amazon_pay', 'cashapp', 'us_bank_account']}} />
      </div>

      {message && <div className="err-banner show">{message}</div>}

      <button
        className="btn"
        onClick={handlePay}
        disabled={!stripe || isLoading || !ready}
        style={{ marginTop: 16 }}
      >
        {!ready ? (
          <><div className="spin spin-sm" /><span>{t.loading}</span></>
        ) : isLoading ? (
          <><div className="spin spin-sm" /><span>{t.processing}</span></>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span>{t.buyNow}</span>
          </>
        )}
      </button>
      <p className="pay-note">
        {t.termsPre}<a href="/agb">{t.termsAgb}</a>{t.termsMid}<a href="/datenschutz">{t.termsPrivacy}</a>{t.termsPost}
      </p>
    </div>
  );
}

function ElementsWrapper({ clientSecret, customerId, visitorId, customerEmail, lang, t }) {
  const options = useMemo(() => ({
    clientSecret,
    locale: 'en',
    paymentMethodOrder: ['amazon_pay', 'cashapp', 'us_bank_account'],
  }), [clientSecret]);

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm customerId={customerId} visitorId={visitorId} customerEmail={customerEmail} t={t} />
    </Elements>
  );
}

function Field({ id, label, type = 'text', value, onChange, error, ...props }) {
  return (
    <div className={`field${error ? ' err' : ''}${value ? ' filled' : ''}`}>
      <input id={id} type={type} placeholder=" " value={value} onChange={onChange} {...props} />
      <label htmlFor={id}>{label}</label>
      {error && <div className="field-err">{error}</div>}
    </div>
  );
}

function SummaryContent({ t }) {
  return (
    <>
      <div className="prod-row">
        <div className="prod-img-wrap">
          <img src={PRODUCT_IMAGE} className="prod-img" alt={PRODUCT_NAME} />
          <span className="qty-badge">1</span>
        </div>
        <div style={{ flex: 1 }}>
          <div className="prod-name">{PRODUCT_NAME}</div>
          <div className="prod-meta">{t.carryOn} · {t.color}</div>
        </div>
        <div className="prod-price" style={{ alignSelf: 'center' }}><span>{PRICE_TEXT}</span></div>
      </div>
      <div className="prod-row">
        <div className="prod-img-wrap">
          <img src="https://air-back.ch/airback.store/cdn/shop/files/Frame_27.jpg" className="prod-img" alt={t.bundleName} />
          <span className="qty-badge">1</span>
        </div>
        <div style={{ flex: 1 }}>
          <div className="prod-name">{t.bundleName}</div>
          <div className="prod-meta">{t.bundleIncluded}</div>
        </div>
        <div className="prod-price" style={{ alignSelf: 'center' }}><span className="bundle-free">{t.bundleFree}</span></div>
      </div>
      <div className="disc-row">
        <input className="disc-in" placeholder={t.discountPlaceholder} />
        <button className="disc-btn">{t.apply}</button>
      </div>
      <div className="divider" />
      <div className="sum-row"><span className="sum-lbl">{t.subtotal}</span><span>{PRICE_TEXT}</span></div>
      <div className="sum-row"><span className="sum-lbl">{t.shipping}</span><span className="sum-free">{t.freeShipping}</span></div>
      <div className="divider" />
      <div className="total-row">
        <span className="total-lbl">{t.total}</span>
        <div className="total-right"><span className="total-val">{PRICE_TEXT}</span></div>
      </div>

      <div className="trust-row">
        <div className="trust-item">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span>{t.trustSSL}</span>
        </div>
        <div className="trust-item">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <span>{t.trustBuyer}</span>
        </div>
        <div className="trust-item">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
          <span>{t.trustReturn}</span>
        </div>
      </div>
    </>
  );
}

export default function App() {
  const [lang, setLang] = useState(detectLang);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', fname: '', lname: '', address: '', zip: '', city: '', state: '', phone: '', country: 'US' });
  const [errors, setErrors] = useState({});
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [deliveryTab, setDeliveryTab] = useState('home');

  const t = I18N[lang] || I18N.en;

  useEffect(() => {
    try { localStorage.setItem('airback_lang', lang); } catch (e) {}
    try { document.documentElement.lang = lang; } catch (e) {}
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', lang);
      window.history.replaceState({}, '', url);
    } catch (e) {}
  }, [lang]);

  useEffect(() => {
    const params0 = new URLSearchParams(window.location.search);
    const isReturn = params0.get('setup_intent_client_secret') && params0.get('redirect_status') === 'succeeded';
    if (!isReturn) {
      fbTrack('InitiateCheckout', { currency: 'USD', value: PRICE_VALUE, content_name: PRODUCT_NAME, content_ids: [PRODUCT_ID] });
    }

    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(geo => {
        fetch(`${BACKEND}/page-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: VISITOR_ID,
            ip: geo.ip,
            country: geo.country_name,
            city: geo.city,
          })
        }).catch(() => {});
      })
      .catch(() => {
        fetch(`${BACKEND}/page-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitorId: VISITOR_ID })
        }).catch(() => {});
      });

    const p = new URLSearchParams(window.location.search);
    const sics = p.get('setup_intent_client_secret');
    const cid  = p.get('customer_id');
    const rs   = p.get('redirect_status');
    const vid  = p.get('visitor_id');
    if (sics && cid && rs === 'succeeded') {
      setIsProcessing(true);
      stripePromise.then(stripe => {
        stripe.retrieveSetupIntent(sics).then(({ setupIntent }) => {
          if (setupIntent.status === 'succeeded') {
            try {
              const once = 'fb_purchase_' + setupIntent.id;
              if (!localStorage.getItem(once)) {
                fbTrack('Purchase', { currency: 'USD', value: PRICE_VALUE, content_name: PRODUCT_NAME, content_ids: [PRODUCT_ID], event_id: setupIntent.id });
                localStorage.setItem(once, '1');
              }
            } catch (e) {
              fbTrack('Purchase', { currency: 'USD', value: PRICE_VALUE, content_name: PRODUCT_NAME, content_ids: [PRODUCT_ID], event_id: setupIntent.id });
            }

            fetch(`${BACKEND}/create-subscription`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customerId: cid,
                paymentMethodId: setupIntent.payment_method,
                setupIntentId: setupIntent.id,
                visitorId: vid || VISITOR_ID
              })
            })
            .then(r => r.json())
            .then(data => {
              setOrderNumber(data.subscriptionId || '0000');
              setOrderComplete(true);
              setIsProcessing(false);
            })
            .catch(() => {
              setOrderNumber('0000');
              setOrderComplete(true);
              setIsProcessing(false);
            });
          } else {
            setIsProcessing(false);
          }
        });
      });
    }
  }, []);

  const setField = key => e => setForm(f => ({ ...f, [key]: e.target.value }));
  const clearErr = key => setErrors(er => ({ ...er, [key]: '' }));

  const validate = () => {
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.errEmail;
    if (!form.fname.trim()) e.fname = t.errFname;
    if (!form.lname.trim()) e.lname = t.errLname;
    if (!form.address.trim()) e.address = t.errAddress;
    if (!form.zip.trim()) e.zip = t.errZip;
    if (!form.city.trim()) e.city = t.errCity;
    if (!form.state.trim()) e.state = t.errState;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleStep1 = async e => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    fbTrack('InitiateCheckout', { currency: 'USD', value: PRICE_VALUE, content_name: PRODUCT_NAME, content_ids: [PRODUCT_ID] });
    try {
      const res = await fetch(`${BACKEND}/create-setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          fname: form.fname,
          lname: form.lname,
          address: form.address,
          zip: form.zip,
          city: form.city,
          state: form.state,
          country: form.country,
          phone: form.phone,
          visitorId: VISITOR_ID,
          lang: 'en',
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setClientSecret(data.clientSecret);
      setCustomerId(data.customerId);
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert(t.errorPrefix + err.message);
    }
    setIsLoading(false);
  };

  if (isProcessing) return (
    <div className="processing-screen">
      <div className="spin" />
      <div className="proc-t">{t.procT}</div>
      <div className="proc-s">{t.procS}</div>
    </div>
  );

  if (orderComplete) return <ThankYouPage orderNumber={orderNumber} lang={lang} />;

  return (
    <div className="app">
      <header className="topbar">
        <a href="/" className="logo">Air<em>back</em></a>
        <div className="topbar-right">
          <div className="secure">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            {t.secure}
          </div>
        </div>
      </header>

      <div className="mob-bar">
        <div className="mob-toggle">
          <div className="mob-toggle-left">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            {t.orderSummary}
          </div>
        </div>
        <div className="mob-body"><SummaryContent t={t} /></div>
      </div>

      <div className="layout">
        <div className="left">
          <nav className="bc">
            <div className={`bc-step${step === 1 ? ' active' : ' done'}`}>
              <div className="bc-num">{step > 1 ? '✓' : '1'}</div>
              {t.step1}
            </div>
            <span className="bc-sep">›</span>
            <div className={`bc-step${step === 2 ? ' active' : ''}`}>
              <div className="bc-num">2</div>
              {t.step2}
            </div>
          </nav>

          {step === 1 && (
            <form onSubmit={handleStep1} noValidate>
              <div className="sec">
                <div className="sec-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  {t.contact}
                </div>
                <Field id="email" label={t.email} type="email" value={form.email} onChange={setField('email')} error={errors.email} onFocus={() => clearErr('email')} />
                <label className="chk"><input type="checkbox" /><span>{t.newsletter}</span></label>
              </div>

              <div className="sec">
                <div className="sec-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16,8 20,8 23,11 23,16 16,16 16,8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  {t.delivery}
                </div>
                <div className="dtabs">
                  {['home', 'dhl'].map(tab => (
                    <div key={tab} className={`dtab${deliveryTab === tab ? ' sel' : ''}`} onClick={() => setDeliveryTab(tab)}>
                      <div className="radio"><div className="radio-inner" /></div>
                      <span className="dtab-label">{tab === 'home' ? t.deliveryHome : t.deliverySignature}</span>
                    </div>
                  ))}
                </div>
                <div className="field filled">
                  <select value={form.country} onChange={setField('country')}>
                    {['US'].map(c => (
                      <option key={c} value={c}>{t.countries[c]}</option>
                    ))}
                  </select>
                  <label>{t.country}</label>
                  <span className="arr">▾</span>
                </div>
                <div className="field-row">
                  <Field id="fname" label={t.fname} value={form.fname} onChange={setField('fname')} error={errors.fname} onFocus={() => clearErr('fname')} />
                  <Field id="lname" label={t.lname} value={form.lname} onChange={setField('lname')} error={errors.lname} onFocus={() => clearErr('lname')} />
                </div>
                <Field id="address" label={t.address} value={form.address} onChange={setField('address')} error={errors.address} onFocus={() => clearErr('address')} />
                <div className="field-row">
                  <Field id="city" label={t.city} value={form.city} onChange={setField('city')} error={errors.city} onFocus={() => clearErr('city')} />
                  <Field id="state" label={t.state} value={form.state} onChange={setField('state')} error={errors.state} onFocus={() => clearErr('state')} />
                </div>
                <Field id="zip" label={t.zip} value={form.zip} onChange={setField('zip')} error={errors.zip} onFocus={() => clearErr('zip')} />
                <Field id="phone" label={t.phone} type="tel" value={form.phone} onChange={setField('phone')} />
                <label className="chk"><input type="checkbox" /><span>{t.saveData}</span></label>
              </div>

              <div className="sec">
                <div className="sec-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  {t.shipping}
                </div>
                <div className="opt-box">
                  <div className="opt-row sel">
                    <div className="radio"><div className="radio-inner" /></div>
                    <div className="opt-lbl"><div className="opt-name">USPS Standard</div><div className="opt-sub">1–2 business days</div></div>
                    <span className="badge badge-free">{t.free}</span>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn" disabled={isLoading}>
                {isLoading
                  ? <><div className="spin spin-sm" /><span>{t.loading}</span></>
                  : <><span>{t.continueBtn}</span><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></>
                }
              </button>
            </form>
          )}

          {step === 2 && (
            <div>
              <div className="s2sum">
                <div className="s2row">
                  <span className="s2lbl">{t.contact}</span>
                  <span className="s2val">{form.email}</span>
                  <span className="s2edit" onClick={() => setStep(1)}>{t.change}</span>
                </div>
                <div className="s2row">
                  <span className="s2lbl">{t.delivery}</span>
                  <span className="s2val">{form.address}, {form.city}, {form.state} {form.zip}</span>
                  <span className="s2edit" onClick={() => setStep(1)}>{t.change}</span>
                </div>
              </div>

              <div className="sec">
                <div className="sec-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  Payment
                </div>

                <p className="pay-subtitle">{t.securedNote}</p>
                <ElementsWrapper clientSecret={clientSecret} customerId={customerId} visitorId={VISITOR_ID} customerEmail={form.email} lang={lang} t={t} />
              </div>
            </div>
          )}

        </div>

        <div className="right"><SummaryContent t={t} /></div>
      </div>
    </div>
  );
}